import React, { CSSProperties } from "react";
import { Platform, TouchableOpacity } from "react-native";
import { Descendant, Editor, Element, Node, Point, Range, Text, Transforms } from "slate";
import { DefaultElement, RenderElementProps, RenderLeafProps } from "slate-react";
import { jsx } from "slate-hyperscript";
import { uniqueId } from "lodash";

import { PdText } from "src/components/layout";
import { SlateEditor, SlateElement, SlateLinkElement } from "src/custom-typings";
import { kFonts } from "src/kFonts";
import { getSafeUrl } from "src/util/functions";
import { getBrandPublic } from "src/util/apiNew";
import { useToast } from "src/hooks/toast";

import { tComposerFile } from "./types";
import { openUrlInNewTab } from "src/util/linking";
import { kColors } from "src/kColors";

interface iComposerContext {
  value?: string;
  from?: string;
  to?: string;
  files?: tComposerFile[];
  canSend?: boolean;
  onSend?: () => void;
  onAddLink?: () => void;
  addMessageFile?: (file: tComposerFile) => void;
  deleteMessageFile?: (index: number) => void;
}

type tElementTag = {
  [key: string]: (el: HTMLElement) => { type: string; url?: string | null };
};

type tTextTag = {
  [key: string]: () => { [key: string]: boolean };
};

export const ComposerContext = React.createContext<iComposerContext>({});

export const useComposer = (): iComposerContext => {
  return React.useContext(ComposerContext);
};

export const defaultInitialValue: Descendant[] = [{ type: "paragraph", children: [{ text: "" }] }];

export const isWeb = Platform.OS === "web";

const elementTags: tElementTag = {
  A: (el: HTMLElement) => ({ type: "link", url: el.getAttribute("href") }),
  BLOCKQUOTE: () => ({ type: "quote" }),
  H1: () => ({ type: "heading-one" }),
  H2: () => ({ type: "heading-two" }),
  H3: () => ({ type: "heading-three" }),
  H4: () => ({ type: "heading-four" }),
  H5: () => ({ type: "heading-five" }),
  H6: () => ({ type: "heading-six" }),
  IMG: (el: HTMLElement) => ({ type: "image", url: el.getAttribute("src") }),
  LI: () => ({ type: "list-item" }),
  OL: () => ({ type: "numbered-list" }),
  P: () => ({ type: "paragraph" }),
  PRE: () => ({ type: "code" }),
  UL: () => ({ type: "bulleted-list" }),
};

// COMPAT: `B` is omitted here because Google Docs uses `<b>` in weird ways.
const textTags: tTextTag = {
  CODE: () => ({ code: true }),
  DEL: () => ({ strikethrough: true }),
  EM: () => ({ italic: true }),
  I: () => ({ italic: true }),
  S: () => ({ strikethrough: true }),
  B: () => ({ bold: true }),
  U: () => ({ underline: true }),
};

export const deserialize = (el: ChildNode): SlateElement | Node | Node[] | string | null => {
  if (el.nodeType === 3) {
    if (el.textContent === `\n`) return null;
    if (el.textContent === `\n\n`) return null;
    return el.textContent;
  } else if (el.nodeType !== 1) {
    return null;
  } else if (el.nodeName === "BR") {
    return "\n";
  }

  const { nodeName } = el;
  let parent = el;

  if (nodeName === "PRE" && el.childNodes[0] && el.childNodes[0].nodeName === "CODE") {
    parent = el.childNodes[0];
  }
  let children = Array.from(parent.childNodes).map(deserialize).flat();

  if (children.length === 0) {
    children = [{ text: "" }];
  }

  if (el.nodeName === "BODY") {
    return jsx("fragment", {}, children);
  }

  if (elementTags[nodeName]) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const attrs = elementTags[nodeName](el as HTMLElement);
    return jsx("element", attrs, children);
  }

  if (textTags[nodeName]) {
    const attrs = textTags[nodeName]();
    return children.map((child) => jsx("text", attrs, child));
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return children;
};

const wrapLink = (editor: SlateEditor, text: string, url: string) => {
  const { selection } = editor;
  const isCollapsed = selection && Range.isCollapsed(selection);
  const link: SlateLinkElement = {
    type: "link",
    url,
    children: isCollapsed ? [{ text }] : [],
  };

  if (isCollapsed) {
    Transforms.insertNodes(editor, [link, { text: " " }]);
  } else {
    Transforms.wrapNodes(editor, link, { split: true });
    Transforms.collapse(editor, { edge: "end" });
  }
};

export const resetEditor = (editor: SlateEditor): void => {
  Transforms.delete(editor, {
    at: {
      anchor: Editor.start(editor, []),
      focus: Editor.end(editor, []),
    },
  });
};

export const renderElement = (props: RenderElementProps): JSX.Element => {
  const { attributes, children, element } = props;

  if (element.type === "link") {
    return (
      <a style={webStyles.aElement} {...attributes} href={element.url}>
        {children}
      </a>
    );
  }

  return <DefaultElement {...props} />;
};

export const renderLeaf = (props: RenderLeafProps): JSX.Element => {
  const { attributes, children, leaf } = props;

  if (leaf.bold) {
    return (
      <span {...attributes}>
        <b style={webStyles.bElement}>{children}</b>
      </span>
    );
  }

  return <span {...attributes}>{children}</span>;
};

export const convertTextToDescendents = (value: string): Descendant[] | undefined => {
  let jsonValue: Descendant[] | undefined = [];

  try {
    jsonValue = JSON.parse(value);
  } catch (error) {
    jsonValue = undefined;
  }

  return jsonValue;
};

const LinkElement: React.FC<{ element: SlateLinkElement; isSender?: boolean }> = ({ element, isSender, children }) => {
  const toast = useToast();

  const handleOnPressUrl = async () => {
    if (element.url.startsWith("openBrandTerms")) {
      try {
        const brandId = element.url.split(":")[1];
        const brand = await getBrandPublic(brandId);
        openUrlInNewTab(getSafeUrl(brand.termsAndConditionsUrl));
      } catch (er) {
        toast({
          description: "It was not possible to load the document right now, please try again later.",
          status: "error",
        });
      }
    } else {
      openUrlInNewTab(getSafeUrl(element.url));
    }
  };

  return (
    <TouchableOpacity onPress={handleOnPressUrl}>
      <PdText variant="body-sm" style={[kFonts.textUnderline, isSender ? { color: kColors.white } : {}]}>
        {children}
      </PdText>
    </TouchableOpacity>
  );
};

export const convertNodeToReact = (node: Node, isSender?: boolean): React.ReactElement => {
  if (Editor.isEditor(node)) return <></>;

  if (Text.isText(node)) {
    return (
      <PdText
        variant="body-sm"
        style={[kFonts.textFullWidth, isSender ? { color: kColors.white } : {}, node.bold ? kFonts.textBold : {}]}
      >
        {!node.text ? "\n" : node.text}
      </PdText>
    );
  }

  const children = node.children.map((n) => (
    <React.Fragment key={uniqueId("node_")}>{convertNodeToReact(n, isSender)}</React.Fragment>
  ));

  switch (node.type) {
    case "link":
      return (
        <LinkElement isSender={isSender} element={node}>
          {children}
        </LinkElement>
      );
    default:
      return (
        <PdText variant="body-sm" style={[kFonts.textFullWidth, isSender ? { color: kColors.white } : {}]}>
          {children}
        </PdText>
      );
  }
};

export const insertLink = (editor: SlateEditor, text: string, url: string): void => {
  wrapLink(editor, text, url);
};

export const insertDataHtml = (editor: SlateEditor, html: string, clear: boolean): void => {
  try {
    const parsed = new DOMParser().parseFromString(html, "text/html");
    const fragment = deserialize(parsed.body);
    if (Node.isNodeList(fragment)) {
      if (clear) {
        Transforms.delete(editor, {
          at: {
            anchor: Editor.start(editor, []),
            focus: Editor.end(editor, []),
          },
        });
        Transforms.insertNodes(editor, fragment, { at: [0] });
      } else {
        Transforms.insertNodes(editor, fragment);
      }
    }
  } catch (er) {
    /** empty */
  }
};

export const withHtml = (editor: SlateEditor): SlateEditor => {
  const { insertData, isInline } = editor;

  editor.isInline = (element) => {
    return element.type === "link" ? true : isInline(element);
  };

  editor.insertData = (data) => {
    const html = data.getData("text/html");

    if (html) {
      insertDataHtml(editor, html, false);
      return;
    }

    insertData(data);
  };

  return editor;
};

export const isEditorEmpty = (editor: SlateEditor): boolean => {
  if (!editor.children?.[0]) return false;
  if (!Element.isElement(editor.children[0])) return false;
  const element = editor.children[0];
  const { children } = element;
  const [first] = children;
  return (
    children.length === 0 ||
    (children.length === 1 && Text.isText(first) && first.text.trim() === "" && !editor.isVoid(element))
  );
};

export const isDescendentsEmpty = (descendents?: Descendant[]): boolean => {
  if (!descendents) return true;
  return (
    (Element.isElement(descendents[0]) && !descendents[0].children[0].text) ||
    (Text.isText(descendents[0]) && !descendents[0].text)
  );
};

const webStyles: { [key: string]: CSSProperties } = {
  aElement: {
    color: "#373938",
    textDecorationLine: "underline",
    padding: 0,
    margin: 0,
  },
  bElement: {
    fontWeight: 500,
  },
};

/**
 * resetNodes resets the value of the editor.
 * It should be noted that passing the `at` parameter may cause a "Cannot resolve a DOM point from Slate point" error.
 */
export const resetNodes = (
  editor: Editor,
  options: {
    nodes?: Node | Node[];
    at?: Location;
  } = {},
): void => {
  const children = [...editor.children];

  children.forEach((node) => editor.apply({ type: "remove_node", path: [0], node }));

  if (options.nodes) {
    const nodes = Node.isNode(options.nodes) ? [options.nodes] : options.nodes;

    nodes.forEach((node, i) => editor.apply({ type: "insert_node", path: [i], node: node }));
  }

  const point = options.at && Point.isPoint(options.at) ? options.at : Editor.end(editor, []);

  if (point) {
    Transforms.select(editor, point);
  }
};
